import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  useTheme,
  alpha,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { addToWaitlist, StudyGoal } from '../../data/waitlist';

const STUDY_GOALS: { value: StudyGoal; label: string }[] = [
  { value: 'medical_boards', label: 'Medical Boards (Step 1, Step 2, etc.)' },
  { value: 'law_exam', label: 'Bar Exam / Law School' },
  { value: 'certification', label: 'Professional Certification' },
  { value: 'language', label: 'Language Learning' },
  { value: 'college', label: 'College / University Courses' },
  { value: 'high_school', label: 'High School' },
  { value: 'other', label: 'Something else' },
];

const inputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'rgba(255,255,255,0.08)',
    borderRadius: 2,
    color: 'white',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
    '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.55)' },
  '& .MuiInputLabel-root.Mui-focused': { color: 'rgba(255,255,255,0.85)' },
  '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.55)' },
};

export function WaitlistSection() {
  const theme = useTheme();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [studyGoal, setStudyGoal] = useState<StudyGoal | ''>('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) {
      setError('Email is required.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await addToWaitlist(email, name, studyGoal);
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box sx={{ py: { xs: 10, md: 14 }, position: 'relative' }}>
      <Container maxWidth="sm">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
        >
          <Box
            sx={{
              p: { xs: 4, md: 6 },
              borderRadius: '24px',
              bgcolor: 'rgba(255,255,255,0.09)',
              border: '1px solid rgba(255,255,255,0.16)',
              boxShadow: [
                '0 20px 60px rgba(0,0,0,0.45)',
                '0 8px 16px rgba(0,0,0,0.28)',
                'inset 0 2px 0 rgba(255,255,255,0.2)',
                'inset 0 -2px 0 rgba(0,0,0,0.18)',
              ].join(', '),
              textAlign: 'center',
            }}
          >
            {submitted ? (
              <Stack spacing={2.5} alignItems="center">
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 56, color: theme.palette.success.main }}
                />
                <Typography variant="h4" fontWeight={800} color="white">
                  You're on the list!
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: 'rgba(255,255,255,0.72)', maxWidth: 380, mx: 'auto' }}
                >
                  We'll email you the moment early access opens. In the meantime, tell a
                  friend — the more the merrier.
                </Typography>
              </Stack>
            ) : (
              <>
                {/* Header */}
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    background: `linear-gradient(145deg, ${theme.palette.warning.main}, ${theme.palette.warning.light})`,
                    boxShadow: [
                      `0 10px 24px ${alpha(theme.palette.warning.main, 0.5)}`,
                      '0 4px 8px rgba(0,0,0,0.22)',
                      'inset 0 2px 0 rgba(255,255,255,0.3)',
                    ].join(', '),
                    color: 'white',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: 30 }} />
                </Box>

                <Typography variant="h4" fontWeight={800} color="white" mb={1.5}>
                  Get early access
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: 'rgba(255,255,255,0.7)', mb: 4, lineHeight: 1.65 }}
                >
                  Join the waitlist and be first in when we open the doors. We're letting
                  in small batches — no spam, just your invite.
                </Typography>

                {/* Form */}
                <Box component="form" onSubmit={handleSubmit}>
                  <Stack spacing={2.5}>
                    <TextField
                      label="First name (optional)"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      fullWidth
                      sx={inputSx}
                    />

                    <TextField
                      label="Email address *"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      fullWidth
                      required
                      sx={inputSx}
                    />

                    <FormControl fullWidth sx={inputSx}>
                      <InputLabel>What are you studying for?</InputLabel>
                      <Select
                        value={studyGoal}
                        label="What are you studying for?"
                        onChange={(e) => setStudyGoal(e.target.value as StudyGoal)}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              bgcolor: theme.palette.primary.dark,
                              color: 'white',
                              '& .MuiMenuItem-root:hover': {
                                bgcolor: 'rgba(255,255,255,0.1)',
                              },
                            },
                          },
                        }}
                      >
                        {STUDY_GOALS.map((g) => (
                          <MenuItem key={g.value} value={g.value}>
                            {g.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {error && (
                      <Typography variant="body2" color="error.light">
                        {error}
                      </Typography>
                    )}

                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={submitting}
                      fullWidth
                      sx={{
                        py: 1.75,
                        fontWeight: 700,
                        fontSize: 'body1.fontSize',
                        borderRadius: 2,
                        background: `linear-gradient(90deg, ${theme.palette.warning.main}, ${theme.palette.warning.light})`,
                        boxShadow: `0 8px 24px ${alpha(theme.palette.warning.main, 0.45)}`,
                        '&:hover': {
                          background: `linear-gradient(90deg, ${theme.palette.warning.dark}, ${theme.palette.warning.main})`,
                        },
                        '&:disabled': { opacity: 0.6 },
                      }}
                    >
                      {submitting ? 'Joining…' : 'Join the waitlist'}
                    </Button>

                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                      No spam. Just your invite when it's ready.
                    </Typography>
                  </Stack>
                </Box>
              </>
            )}
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
}
